import { ExternalLink, Page, Tabs } from '@ui/themed';
import { t } from 'i18next';
import { type ReactNode, useState } from 'react';
import { useEnv } from '../../../context/env-context';
import { useHandlers, useUser, withPageProviders } from '../../../context/page-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { CoreTab } from '../components/core-tab';
import { CssTab } from '../components/css-tab';
import { PdpTab } from '../components/pdp-tab';
import { TipsTab } from '../components/tips-tab';
import type { WidgetTab } from '../constants';
import { WIDGET_TABS } from '../constants';
import { isShopify, isWix, isWoocommerce, themeSetupUrl } from '../utils/platform';

const TAB_CONTENT: Record<WidgetTab, ReactNode> = {
  core: <CoreTab />,
  pdp: <PdpTab />,
  css: <CssTab />,
  tips: <TipsTab />,
};

const PAGE = 'features:storefront_widgets.pages.storefront_widgets_page' satisfies I18nPrefix;

export const StorefrontWidgetsPage = withPageProviders(StorefrontWidgetsPageContent);

function StorefrontWidgetsPageContent() {
  const env = useEnv();
  const { isSparkStaff } = useUser();
  const { navigationHandler } = useHandlers();

  const shopify = isShopify(env.platform);
  const tabs = WIDGET_TABS.filter((tab) => {
    if (tab === 'pdp' && isWix(env.platform)) return false;
    if (tab === 'tips' && (isWix(env.platform) || isWoocommerce(env.platform))) return false;
    return true;
  }).map((tab) => ({ value: tab, label: t(`${PAGE}.tabs.${tab}`) }));

  const slug = navigationHandler.path().split('/').filter(Boolean).at(-1);
  const initialTab: WidgetTab = (WIDGET_TABS as readonly string[]).includes(slug ?? '')
    ? (slug as WidgetTab)
    : 'core';

  const [activeTab, setActiveTab] = useState<WidgetTab>(initialTab);

  const showTabNav = isSparkStaff || !shopify;
  const themeSetupAdminUrl = themeSetupUrl(env);

  const description =
    shopify && themeSetupAdminUrl ? (
      <>
        {t(`${PAGE}.header.shopify_description`)}{' '}
        <ExternalLink showIcon link={themeSetupAdminUrl}>
          {t(`${PAGE}.header.shopify_admin`)}
        </ExternalLink>
      </>
    ) : (
      t(`${PAGE}.header.description`)
    );

  return (
    <Page
      title={t(`${PAGE}.header.title`)}
      description={description}
      learnMoreUrl={shopify ? undefined : 'https://docs.sparklayer.io/frontend-integration'}
    >
      <Tabs
        tabs={tabs}
        value={activeTab}
        showNav={showTabNav}
        onChange={(tab) => {
          setActiveTab(tab);
          navigationHandler.navigate(`/storefront/widgets/${tab}`);
        }}
      >
        {tabs.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value}>
            {TAB_CONTENT[tab.value]}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Page>
  );
}
